import './traceTree.scss';

import { Badge, Box, Button } from '@cloudscape-design/components';
import { travelerLastStepPassed } from '@parallel-fluidics/shared/models/travelers';
import PPartDisplay from 'components/Projects/pPartDisplay';
import LotDisplay from 'features/lots/components/LotDisplay';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * One lot in the trace tree: the lot row, the travelers it was
 * installed on (in stock vs shipped shown per row), then its assembled
 * children indented one level. Renders straight from the walk result —
 * no per-node fetching. Row layout and the └ connectors live in
 * traceTree.scss.
 */
export default function TraceLotNode({
  trace, lotId, depth = 0, ancestorLotIds = [],
}) {
  const navigate = useNavigate();
  const lot = trace.lotsById[lotId];
  if (!lot) return null;

  // A lot that appears in its own ancestry is a cycle (impossible from
  // the app's flows today, but manual edits — or a future edit-sources —
  // could create one). Render a marker instead of recursing forever.
  if (ancestorLotIds.includes(lotId)) {
    return (
      <div className="trace-node">
        <div className="trace-row is-child">
          <div className="trace-row-content">
            <LotDisplay lot={lot} link />
            <Box color="text-status-inactive" fontSize="body-s">
              cycle — already shown above
            </Box>
          </div>
        </div>
      </div>
    );
  }

  const travelers = (trace.travelerIdsByLotId[lotId] || [])
    .map((travelerId) => trace.travelersById[travelerId]);
  const childLotIds = trace.childLotIdsByLotId[lotId] || [];

  return (
    <div className="trace-node">
      <div className={depth > 0 ? 'trace-row is-child' : 'trace-row'}>
        <div className="trace-row-content">
          <LotDisplay lot={lot} link showPart showKind showStatus showQty />
        </div>
      </div>
      {travelers.map((traveler) => {
        let badge = <Badge color="blue">In progress</Badge>;
        if (traveler.shipped) badge = <Badge color="grey">Shipped</Badge>;
        else if (travelerLastStepPassed(traveler.steps)) badge = <Badge color="green">Completed</Badge>;
        return (
          <div key={traveler.id} className="trace-row is-child is-traveler">
            <div className="trace-row-content">
              <Button variant="inline-link" onClick={() => navigate(`/travelers/${traveler.id}`)}>
                {`Traveler ${traveler.id}`}
              </Button>
              <PPartDisplay
                fieldName="fullId"
                projectId={traveler.project}
                pPartId={traveler.pPart}
                fallbackMessage="—"
              />
              {badge}
              {traveler.order && (
                <Box color="text-body-secondary" fontSize="body-s">{`Order ${traveler.order}`}</Box>
              )}
            </div>
          </div>
        );
      })}
      {childLotIds.map((childLotId) => (
        <TraceLotNode
          key={childLotId}
          trace={trace}
          lotId={childLotId}
          depth={depth + 1}
          ancestorLotIds={[...ancestorLotIds, lotId]}
        />
      ))}
    </div>
  );
}

const traceShape = PropTypes.shape({
  lotsById: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string,
    code: PropTypes.string,
    status: PropTypes.string,
    kind: PropTypes.string,
    remainingQty: PropTypes.number,
    initialQty: PropTypes.number,
    projectDocId: PropTypes.string,
    pPartDocId: PropTypes.string,
  })).isRequired,
  travelersById: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string,
    shipped: PropTypes.bool,
    order: PropTypes.string,
    project: PropTypes.string,
    pPart: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({})),
  })).isRequired,
  childLotIdsByLotId: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  travelerIdsByLotId: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
});

TraceLotNode.propTypes = {
  trace: traceShape.isRequired,
  lotId: PropTypes.string.isRequired,
  depth: PropTypes.number,
  ancestorLotIds: PropTypes.arrayOf(PropTypes.string),
};
